import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
// Hooks
import { useCreateResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useCreateResource';
import { useDeleteResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useDeleteResource';
// Types
import { ApprovalStatusType } from '@cube3/common/model/schema/resources/approval';
// State
import { ReviewViewContext } from '../Context/reviewViewContext';

// UI
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';
import { useReviewApprovals } from '../Hooks/useReviewApprovals';
import { Status } from '@cube3/cubicle/src/core/atoms/Status/Status';
import MaybeTooltip from '@cube3/cubicle/src/core/atoms/Tooltip/MaybeTooltip';
import Select from '@cube3/cubicle/src/core/atoms/Select/Select';
import { Option } from '@cube3/cubicle/src/core/atoms/Select/Option';
import { userActivationContext } from '../../Workspace/contexts/UserActivationContext';
import { useMutateResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useMutateResource';
// import { List } from '@cube3/cubicle/src/core/atoms/List';
// import { ListItem } from '@cube3/cubicle/src/core/atoms/ListItem';

type RatingType = ApprovalStatusType | 'pending';

const ratings = [
  {
    id: 'approved' as const,
    value: 'Approved'
  },
  {
    id: 'rejected' as const,
    value: 'Rejected'
  },
  {
    id: 'pending' as const,
    value: 'Pending'
  }
];
export const ReviewRatingSettings = React.memo(() => {
  const { activeAsset, reviewLink, email } = useContext(ReviewViewContext);
  const isUserActive = useContext(userActivationContext);

  const { approvals } = useReviewApprovals(
    activeAsset?.id,
    reviewLink?.id,
    isUserActive
  );

  const myApproval = useMemo(() => {
    if (!approvals.resources || !reviewLink) return undefined;
    return approvals.resources.find((a) => a.approver_name === email);
  }, [approvals.resources, reviewLink]);

  const { approved, rejected } = useMemo(() => {
    const approved = approvals.resources?.filter(
      (a) => a.status === 'approved'
    );
    const rejected = approvals.resources?.filter(
      (a) => a.status === 'rejected'
    );
    return {
      approved,
      rejected
    };
  }, [approvals.resources]);

  const cacheInvalidator = useCallback(
    (r) => [
      r,
      {
        type: 'content-tree-node',
        id: activeAsset?.id,
        relationship: 'approvals'
      },
      { type: 'share', id: reviewLink?.id, relationship: 'activities' }
    ],
    [activeAsset, reviewLink]
  );

  const [createApproval] = useCreateResource__ALPHA({
    resourceType: 'approval',
    cacheInvalidator
  });
  const [mutateResource] = useMutateResource__ALPHA({
    cacheInvalidator
  });
  const [deleteResource] = useDeleteResource__ALPHA({
    cacheInvalidator
  });

  const [selectedRating, setSelectedRating] = useState<RatingType>(
    myApproval?.status
  );
  /**
   * - `POST` if there's no existing approval
   * - otherwise
   *    - `PATCH` to change the status
   *    - `DELETE` to set the status back to 'pending'
   */
  const onChangeRating = useCallback(
    (status: RatingType) => {
      if (myApproval) {
        if (myApproval.status === status) return;
        if (status === 'pending') {
          deleteResource({
            type: 'approval',
            id: myApproval.id
          });
        } else {
          mutateResource({
            type: 'approval',
            id: myApproval.id,
            status: status
          });
        }
      } else {
        createApproval({
          type: 'approval',
          status: status,
          relationships: {
            node: {
              data: {
                id: activeAsset?.id,
                type: 'node'
              }
            },
            share: {
              data: {
                type: 'share',
                id: reviewLink?.id
              }
            }
          }
        });
      }
      setSelectedRating(status);
    },
    [
      mutateResource,
      createApproval,
      deleteResource,
      setSelectedRating,
      myApproval,
      activeAsset,
      reviewLink
    ]
  );

  // show selectedRating before the new rating gets saved
  useEffect(() => {
    setSelectedRating(myApproval?.status || selectedRating);
  }, [myApproval, selectedRating]);

  // to update default rating when switching bewteen asssets
  useEffect(() => {
    setSelectedRating(myApproval?.status || 'pending');
  }, [activeAsset]);

  const isPending = !approved?.length && !rejected?.length;

  return (
    <div style={{ marginBottom: '8px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px'
        }}
      >
        <Typography>My Rating</Typography>
        <Select
          placeholder={'Ratings'}
          value={selectedRating}
          // onChange={onChangeRating}
          width={44}
        >
          {ratings.map((f) => {
            return (
              <Option
                key={f.id}
                value={f.id}
                onClick={() => onChangeRating(f.id)}
                startAdornment={<Status status={f.id} />}
              />
            );
          })}
        </Select>
      </div>

      {/** Overall rating */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: '16px'
        }}
      >
        <Typography>Overall Rating</Typography>

        {isPending ? (
          <Status status={'pending'} />
        ) : (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
          >
            {approved?.length ? (
              <MaybeTooltip
                title={
                  <TooltipComponent
                    list={approved}
                    you={email}
                    title={'Approved by:'}
                  />
                }
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <Typography color="dark" colorVariant="02">
                    {approved.length}
                  </Typography>
                  <Status status={'approved'} />
                </div>
              </MaybeTooltip>
            ) : null}
            {rejected?.length ? (
              <MaybeTooltip
                title={
                  <TooltipComponent
                    list={rejected}
                    you={email}
                    title={'Rejected by:'}
                  />
                }
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <Typography color="dark" colorVariant="02">
                    {rejected.length}
                  </Typography>
                  <Status status={'rejected'} />
                </div>
              </MaybeTooltip>
            ) : null}
          </span>
        )}
      </div>
      <Typography color="dark" colorVariant="02">
        {isPending
          ? 'Requires a review'
          : rejected?.length
          ? `Rejected because of ${rejected.length} rejection${
              rejected.length > 1 ? 's' : ''
            }`
          : `Approved because of  ${approved.length} approval${
              approved.length > 1 ? 's' : ''
            }`}
      </Typography>
    </div>
  );
});

const TooltipComponent = ({ list, title = '', you }) => {
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
      <Typography>{title}</Typography>
      {list.map((a) => (
        <li key={a.id} style={{ padding: 0, margin: 0 }}>
          <Typography>
            {a.approver_name === you ? 'You' : a.approver_name}
          </Typography>
        </li>
      ))}
    </ul>
  );
};
